/**
 * Утилиты для работы с регионами
 */

// Маппинг ID департаментов на регионы
export const DEPARTMENT_REGION_MAP = {
  '20': 'СПБ', // Департамент НЦ
  '21': 'СПБ', // Департамент ОС
  '24': 'МСК', // Московский филиал
  '25': 'КРД', // Краснодарский филиал
  '31': 'СПБ', // Стажеры НЦ
  '32': 'СПБ', // Стажеры ОС
  '27': 'СПБ', // ЛО
  '28': 'СПБ', // ЛО
};

// Маппинг родительских департаментов на регионы
export const PARENT_DEPARTMENT_REGION_MAP = {
  '20': 'СПБ', // НЦ
  '21': 'СПБ', // ОС
  '24': 'МСК', // Московский филиал
  '25': 'КРД', // Краснодарский филиал
};

// Маппинг сокращенных названий регионов на полные (для БД)
export const REGION_SHORT_TO_FULL_MAP = {
  'СПБ': 'Санкт-Петербург',
  'МСК': 'Москва',
  'КРД': 'Краснодар',
};

// Маппинг полных названий регионов на сокращенные
export const REGION_FULL_TO_SHORT_MAP = {
  'Санкт-Петербург': 'СПБ',
  'СПБ': 'СПБ',
  'Москва': 'МСК',
  'МСК': 'МСК',
  'Краснодар': 'КРД',
  'КРД': 'КРД',
};

/**
 * Преобразует сокращенное название региона в полное
 * @param {string} shortRegion - Сокращенное название (СПБ, МСК, КРД)
 * @returns {string} - Полное название региона
 */
export const getFullRegionName = (shortRegion) => {
  if (!shortRegion) return 'Санкт-Петербург';
  return REGION_SHORT_TO_FULL_MAP[shortRegion] || 'Санкт-Петербург';
};

/**
 * Преобразует полное название региона в сокращенное
 * @param {string} fullRegion - Полное название региона
 * @returns {string} - Сокращенное название региона
 */
export const getShortRegionName = (fullRegion) => {
  if (!fullRegion) return 'СПБ';
  return REGION_FULL_TO_SHORT_MAP[fullRegion] || 'СПБ';
};

/**
 * Получает bitrix_id региона по сокращенному названию
 * @param {string} shortRegion - Сокращенное название региона
 * @returns {string} - bitrix_id региона
 */
export const getRegionBitrixId = (shortRegion) => {
  const regionBitrixMap = {
    'СПБ': '1', // Санкт-Петербург
    'МСК': '2', // Москва
    'КРД': '3', // Краснодар
  };
  return regionBitrixMap[shortRegion] || '3';
};

/**
 * Получает bitrix_id региона по полному названию
 * @param {string} fullRegion - Полное название региона
 * @returns {string} - bitrix_id региона
 */
export const getRegionBitrixIdByFullName = (fullRegion) => {
  const fullToBitrixMap = {
    'Санкт-Петербург': '1',
    'СПБ': '1',
    'Москва': '2',
    'МСК': '2',
    'Краснодар': '3',
    'КРД': '3',
  };
  return fullToBitrixMap[fullRegion] || '3';
};

/**
 * Получает регион на основе департамента пользователя
 * @param {Array} departmentIds - Массив ID департаментов пользователя
 * @returns {Promise<string>} - Промис с кодом региона
 */
export const getRegionFromDepartments = async (departmentIds) => {
  console.log(`🔍 [getRegionFromDepartments] Входные данные:`, departmentIds);

  if (!departmentIds || departmentIds.length === 0) {
    console.warn('⚠️ Список департаментов пуст');
    return 'СПБ'; // Значение по умолчанию
  }

  console.log(`🔍 [getRegionFromDepartments] DEPARTMENT_REGION_MAP:`, DEPARTMENT_REGION_MAP);

  // Сначала проверяем прямое соответствие департамента
  for (const deptId of departmentIds) {
    const deptIdStr = deptId.toString();
    console.log(`🔍 [getRegionFromDepartments] Проверяем deptIdStr: ${deptIdStr}`);

    if (DEPARTMENT_REGION_MAP[deptIdStr]) {
      const region = DEPARTMENT_REGION_MAP[deptIdStr];
      console.log(`✅ Найден регион для departmentId ${deptIdStr}: ${region}`);
      return region;
    } else {
      console.log(`❌ DEPARTMENT_REGION_MAP[${deptIdStr}] не найден`);
    }
  }

  // Если прямое соответствие не найдено, проверяем родительские департаменты
  const BITRIX_WEBHOOK_URL = process.env.REACT_APP_BITRIX_WEBHOOK_URL || process.env.REACT_APP_PATH_TO_BACKEND;
  console.log(`🔍 [getRegionFromDepartments] Прямое соответствие не найдено для департаментов: ${departmentIds.join(', ')}`);
  console.log(`🔍 [getRegionFromDepartments] PARENT_DEPARTMENT_REGION_MAP:`, PARENT_DEPARTMENT_REGION_MAP);

  for (const deptId of departmentIds) {
    try {
      const response = await fetch(`${BITRIX_WEBHOOK_URL}/api/get-department-info?departmentId=${deptId}`, {
        credentials: "include",
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`🔍 [getRegionFromDepartments] Ответ для департамента ${deptId}:`, result);

        if (result.success && result.data && result.data.PARENT) {
          const parentId = result.data.PARENT.toString();
          console.log(`✅ Департамент ${deptId} имеет parent_id: ${parentId}`);

          if (PARENT_DEPARTMENT_REGION_MAP[parentId]) {
            const region = PARENT_DEPARTMENT_REGION_MAP[parentId];
            console.log(`🎯 Найден регион для parent_id ${parentId}: ${region}`);
            return region;
          } else {
            console.log(`⚠️ parent_id ${parentId} не найден в PARENT_DEPARTMENT_REGION_MAP`);
          }
        } else {
          console.log(`⚠️ Нет данных PARENT для департамента ${deptId}:`, result);
        }
      } else {
        console.log(`⚠️ Не удалось получить данные департамента ${deptId}, статус: ${response.status}`);
      }
    } catch (err) {
      console.warn(`❌ Ошибка при получении данных департамента ${deptId}:`, err);
      continue;
    }
  }

  console.warn(`Регион не определен для департаментов: ${departmentIds.join(', ')}`);
  return 'СПБ'; // Значение по умолчанию
};

/**
 * Получает регион на основе ID ответственного в сделке
 * @param {string} dealId - ID сделки
 * @returns {Promise<string>} - Промис с кодом региона (СПБ, МСК, КРД)
 */
export const getRegionFromDeal = async (dealId) => {
  console.log(`🔍 [getRegionFromDeal] Начинаем определение региона для сделки: ${dealId}`);

  if (!dealId) {
    console.warn('⚠️ ID сделки не указан');
    return 'СПБ'; // Значение по умолчанию
  }

  try {
    // Получаем информацию о сделке
    console.log(`🔍 [getRegionFromDeal] Запрашиваем данные сделки ${dealId}...`);
    const dealResponse = await fetch(
      `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-deal?dealId=${dealId}`,
      { credentials: "include" }
    );

    if (!dealResponse.ok) {
      throw new Error('Не удалось получить данные сделки');
    }

    const dealResult = await dealResponse.json();

    if (!dealResult.success) {
      throw new Error(dealResult.message || 'Ошибка при получении сделки');
    }

    const assignedById = dealResult.data.ASSIGNED_BY_ID;
    console.log(`✅ [getRegionFromDeal] ASSIGNED_BY_ID: ${assignedById}`);

    if (!assignedById) {
      console.warn('⚠️ Ответственный не указан в сделке');
      return 'СПБ';
    }

    // Получаем информацию о пользователе (ответственном)
    console.log(`🔍 [getRegionFromDeal] Запрашиваем данные пользователя ${assignedById}...`);
    const userResponse = await fetch(
      `${process.env.REACT_APP_PATH_TO_BACKEND}/api/get-user?userId=${assignedById}`,
      { credentials: "include" }
    );

    if (!userResponse.ok) {
      throw new Error('Не удалось получить данные пользователя');
    }

    const userResult = await userResponse.json();

    if (!userResult.success) {
      throw new Error(userResult.message || 'Ошибка при получении пользователя');
    }

    const departmentIds = userResult.data.UF_DEPARTMENT;
    console.log(`✅ [getRegionFromDeal] UF_DEPARTMENT:`, departmentIds);

    if (!departmentIds || departmentIds.length === 0) {
      console.warn('⚠️ Пользователь не привязан к департаментам');
      return 'СПБ';
    }

    // Определяем регион на основе департаментов
    console.log(`🔍 [getRegionFromDeal] Вызываем getRegionFromDepartments с departmentIds:`, departmentIds);
    const region = await getRegionFromDepartments(departmentIds);
    console.log(`🎯 [getRegionFromDeal] Для сделки ${dealId} определен регион: ${region}`);

    return region;
  } catch (err) {
    console.error('❌ Ошибка при определении региона из сделки:', err);
    return 'СПБ'; // Значение по умолчанию в случае ошибки
  }
};

/**
 * Получает bitrix_id региона на основе ID сделки
 * @param {string} dealId - ID сделки
 * @returns {Promise<string>} - Промис с bitrix_id региона (1, 2, 3)
 */
export const getRegionBitrixIdFromDeal = async (dealId) => {
  const regionShort = await getRegionFromDeal(dealId);
  return getRegionBitrixId(regionShort);
};
